import * as React from "react";
import { Refine } from "@refinedev/core";
import routerProvider from "@refinedev/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Outlet,
  Route,
  RouterProvider,
} from "react-router";
import { Sidebar } from "@/components/Sidebar";
import { AuthGuard } from "@/components/AuthGuard";
import { GlobalErrorHandler } from "@/components/GlobalErrorHandler";
import { RoleRedirect } from "@/components/RoleRedirect";
import { authProvider } from "@/providers/authProvider";
import { dataProvider } from "@/providers/dataProvider";
import { accessControlProvider } from "@/providers/accessControlProvider";
import { notificationProvider, ToastContainer } from "@/providers/notificationProvider";
import { LoginPage } from "@/pages/auth/login";
import { UsersList } from "@/pages/users/list";
import { UsersShow } from "@/pages/users/show";
import { OperatorsList } from "@/pages/operators/list";
import { OperatorsEdit } from "@/pages/operators/edit";
import { AuditList } from "@/pages/audit/list";
import { PlacesList } from "@/pages/places/list";
import { LocalityList } from "@/pages/localities/list";
import { UsageList } from "@/pages/usage/list";
import { PicksLayout } from "@/components/picks/PicksLayout";
import { PicksSessionReset } from "@/components/picks/PicksSessionReset";
import { RouteGuard } from "@/components/picks/RouteGuard";

const CatalogPage = React.lazy(() =>
  import("@/pages/picks/CatalogPage").then((m) => ({ default: m.CatalogPage })),
);
const PickCreatePage = React.lazy(() =>
  import("@/pages/picks/PickCreatePage").then((m) => ({
    default: m.PickCreatePage,
  })),
);
const PickEditorPage = React.lazy(() =>
  import("@/pages/picks/PickEditorPage").then((m) => ({
    default: m.PickEditorPage,
  })),
);
const PartnersPage = React.lazy(() =>
  import("@/pages/picks/PartnersPage").then((m) => ({
    default: m.PartnersPage,
  })),
);
const CategoriesIconsPage = React.lazy(() =>
  import("@/pages/picks/CategoriesIconsPage").then((m) => ({
    default: m.CategoriesIconsPage,
  })),
);

const GuidesListPage = React.lazy(() =>
  import("@/pages/guides/GuidesListPage").then((m) => ({
    default: m.GuidesListPage,
  })),
);
const GuideEditorPage = React.lazy(() =>
  import("@/pages/guides/GuideEditorPage").then((m) => ({
    default: m.GuideEditorPage,
  })),
);
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
});

function PlaceholderPage({ title }: { title: string }): React.JSX.Element {
  return (
    <div className="flex items-center justify-center flex-1 p-6">
      <p className="text-[var(--muted)] text-[15px]">
        {title} — Раздел в разработке
      </p>
    </div>
  );
}

function AppLayout(): React.JSX.Element {
  return (
    <div className="flex min-h-screen bg-[var(--bg)]">
      <Sidebar />
      <main className="flex-1 ml-[240px] flex flex-col min-h-screen">
        <Outlet />
      </main>
    </div>
  );
}

function NotFound(): React.JSX.Element {
  return (
    <div className="flex items-center justify-center flex-1 p-6">
      <p className="text-[var(--muted)] text-[15px]">Страница не найдена</p>
    </div>
  );
}

function AdminRouteRoot(): React.JSX.Element {
  return (
    <Refine
      authProvider={authProvider}
      dataProvider={dataProvider}
      accessControlProvider={accessControlProvider}
      notificationProvider={notificationProvider}
      routerProvider={routerProvider}
      resources={[
        { name: "users", list: "/users", show: "/users/show/:id" },
        { name: "entitlement", show: "/users/show/:id", meta: { hide: true } },
        { name: "operators", list: "/operators", edit: "/operators/edit/:id" },
        { name: "audit", list: "/audit" },
        { name: "places", list: "/places" },
        { name: "localities", list: "/localities" },
        { name: "usage", list: "/usage" },
        { name: "picks", list: "/picks", create: "/picks/new", show: "/picks/:id/:tab?" },
        { name: "picks-partners", list: "/picks/partners", meta: { label: "Partners" } },
        { name: "picks-categories", list: "/picks/categories", meta: { label: "Categories & Icons" } },
        { name: "guides", list: "/guides", create: "/guides/new", show: "/guides/:id/:tab" },
      ]}
      options={{
        syncWithLocation: true,
        warnWhenUnsavedChanges: false,
        disableTelemetry: true,
      }}
    >
      <GlobalErrorHandler />
      <PicksSessionReset />
      <Outlet />
      <ToastContainer />
    </Refine>
  );
}

const adminRouter = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<AdminRouteRoot />}>
      <Route path="/login" element={<LoginPage />} />
      <Route
        element={
          <AuthGuard>
            <AppLayout />
          </AuthGuard>
        }
      >
        <Route
          path="/users"
          element={
            <RoleRedirect resource="users">
              <UsersList />
            </RoleRedirect>
          }
        />
        <Route
          path="/users/show/:id"
          element={
            <RoleRedirect resource="users" action="show">
              <UsersShow />
            </RoleRedirect>
          }
        />
        <Route
          path="/operators"
          element={
            <RoleRedirect resource="operators">
              <OperatorsList />
            </RoleRedirect>
          }
        />
        <Route
          path="/operators/edit/:id"
          element={
            <RoleRedirect resource="operators" action="edit">
              <OperatorsEdit />
            </RoleRedirect>
          }
        />
        <Route
          path="/audit"
          element={
            <RoleRedirect resource="audit">
              <AuditList />
            </RoleRedirect>
          }
        />
        <Route
          path="/places"
          element={
            <RoleRedirect resource="places">
              <PlacesList />
            </RoleRedirect>
          }
        />
        <Route
          path="/localities"
          element={
            <RoleRedirect resource="localities">
              <LocalityList />
            </RoleRedirect>
          }
        />
        <Route
          path="/usage"
          element={
            <RoleRedirect resource="usage">
              <UsageList />
            </RoleRedirect>
          }
        />
        <Route
          path="/guides"
          element={
            <RoleRedirect resource="guides">
              <React.Suspense fallback={null}>
                <GuidesListPage />
              </React.Suspense>
            </RoleRedirect>
          }
        />
        <Route
          path="/guides/new"
          element={
            <RoleRedirect resource="guides" action="create">
              <React.Suspense fallback={null}>
                <GuideEditorPage />
              </React.Suspense>
            </RoleRedirect>
          }
        />
        <Route
          path="/guides/:id/:tab"
          element={
            <RoleRedirect resource="guides" action="show">
              <React.Suspense fallback={null}>
                <GuideEditorPage />
              </React.Suspense>
            </RoleRedirect>
          }
        />
        <Route element={<PicksLayout />}>
          <Route
            path="/picks"
            element={
              <RouteGuard resource="picks" action="list">
                <React.Suspense fallback={null}>
                  <CatalogPage />
                </React.Suspense>
              </RouteGuard>
            }
          />
          <Route
            path="/picks/new"
            element={
              <RouteGuard resource="picks" action="edit">
                <React.Suspense fallback={null}>
                  <PickCreatePage />
                </React.Suspense>
              </RouteGuard>
            }
          />
          <Route
            path="/picks/partners"
            element={
              <RouteGuard resource="picks-partners" action="list">
                <React.Suspense fallback={null}>
                  <PartnersPage />
                </React.Suspense>
              </RouteGuard>
            }
          />
          <Route
            path="/picks/categories"
            element={
              <RouteGuard resource="picks-categories" action="list">
                <React.Suspense fallback={null}>
                  <CategoriesIconsPage />
                </React.Suspense>
              </RouteGuard>
            }
          />
          <Route
            path="/picks/:id/:tab?"
            element={
              <RouteGuard resource="picks" action="show">
                <React.Suspense fallback={null}>
                  <PickEditorPage />
                </React.Suspense>
              </RouteGuard>
            }
          />
        </Route>
        <Route path="/moderation" element={<PlaceholderPage title="Модерация" />} />
        <Route path="/dashboard" element={<PlaceholderPage title="Дашборд" />} />
        <Route path="/" element={<UsersList />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Route>,
  ),
);

export function App(): React.JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={adminRouter} />
    </QueryClientProvider>
  );
}
